import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { Configuration, LaunchDarklyService, NotificationService, Organization, OrganizationState } from 'remarketing-angular-library';
import { DropdownSelectComponent } from 'remarketing-ui-components-lib';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';


@Component({
    selector: 'app-address-change-form',
    templateUrl: './address-change-form.component.html',
    styleUrls: ['./address-change-form.component.scss'],
    imports: [TranslocoDirective, FormsModule, ReactiveFormsModule, DropdownSelectComponent]
})
export class AddressChangeFormComponent {  configuration: Configuration;
  private readonly destroyed$ = new Subject();

  @Output() cancelEditing = new EventEmitter();
  @Output() continueEditing = new EventEmitter();
  @Output() closed = new EventEmitter<boolean>();
  @Output() updated = new EventEmitter<boolean>();
  @ViewChild('statesDropdownSelect') statesDropdownSelect: DropdownSelectComponent;
  @ViewChild('provincesDropdownSelect') provincesDropdownSelect: DropdownSelectComponent;
  @Output() sendError = new EventEmitter<boolean>();

  public addressForm: UntypedFormGroup;
  showError = false;

  @Input() variant: 'title' | 'physical';
  @Input() open = false;

  organization: Organization;
  location: string

  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
  };

  states: Array<any>
  provinces: Array<any>
  countries: Array<any>

  get isTitle() {
    return this.variant === 'title';
  }

  get isPhysical() {
    return this.variant === 'physical';
  }

  get isCanadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get countryPayload() {
    return this.isCanadianTenantEnabled ? 'CA' : 'US';
  }

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store,
    private readonly notificationService: NotificationService,
    private readonly translocoService: TranslocoService,
    private readonly launchDarklyService: LaunchDarklyService
  ) {}

  ngOnInit() {
    this.location = this.checkPhysicalOrTitleState();
    this.states = this.initializeStates();
    this.provinces = this.initializeProvinces();
    this.countries = [
      {
        options: [
          { name: 'Canada', value: 'CA', selected: false },
          { name: 'United States', value: 'US', selected: false }
        ]
      }
    ];
    this.getTranslations();
    this.store
      .select(_state => _state.org)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(org => {
        this.organization = org.organization;
        this.address = this.getAddress();
        this.addressForm = this.getAddressForm();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.variant) {
      this.resetForm();
      this.ngOnInit();
    }
  }

  checkPhysicalOrTitleState() {
    const physicalState = this.store.selectSnapshot(OrganizationState.organizationState).organization?.physicalAddress.state;
    const titleState = this.store.selectSnapshot(OrganizationState.organizationState).organization?.mailingAddress.state;
    if (this.isPhysical) {
      return physicalState;
    } else {
      return titleState;
    }
  }

  setStateOrProvince(state) {
    if (this.location) {
      return state === this.location;
    }
    else if (state === '-') {
      return true;
    } else {
      return false;
    }
  }

  updateStateOrProvince(value) {
    if (value) {
      this.addressForm.get('state').setValue(value);
    }
  }

  submit() {
    const payload = this.buildPayload();
    this.showError = false;
    this.notificationService.updateAddress(payload).pipe(take(1)).subscribe(
      () => {
        this.success();
      },
      error => {
        this.showError = true;
        this.sendError.emit(this.showError);
        console.log(error);
      }
    );
  }

  buildPayload(): any {
    return {
      addressType: this.variant === 'physical' ? 'PHYSICAL_ADDRESS' : 'TITLE_ADDRESS',
      currentAddress: {
        name: this.organization.oeName,
        addressLine1: this.address.addressLine1,
        addressLine2: this.address.addressLine2,
        city: this.address.city,
        state: this.address.state,
        zip: this.address.zip,
        country: this.addressForm.get('country').value
      },
      newAddress: {
        name: this.addressForm.get('oeName').value,
        addressLine1: this.addressForm.get('addressLine1').value,
        addressLine2: this.addressForm.get('addressLine2').value,
        city: this.addressForm.get('city').value,
        state: this.addressForm.get('state').value,
        zip: this.addressForm.get('zip').value,
        country: this.addressForm.get('country').value
      },
      dealershipId: this.organization.oeUuid,
      externalDealerNumber: this.organization.organizationProfile.externalId
    };
  }

  resetForm() {
    if (this.addressForm) {
      this.addressForm
        .get('oeName')
        .setValue(this.organization && this.organization.oeName ? this.organization.oeName : '');
      this.addressForm.get('addressLine1').setValue(this.address.addressLine1);
      this.addressForm.get('city').setValue(this.address.city);
      this.addressForm.get('state').setValue(this.address.state);
      this.addressForm.get('zip').setValue(this.address.zip);
      this.addressForm.get('country').setValue(this.address.country || 'US');
    }
  }

  getAddress(): any {
    return this.organization ? this.getAddressFromOrganization()
      : { addressLine1: '', addressLine2: '', city: '', state: '-', zip: '', country: this.countryPayload };
  }

  getAddressFromOrganization(): any {
    return this.variant === 'physical'
      ? this.organization.physicalAddress
      : this.organization.mailingAddress;
  }

  getAddressForm(): UntypedFormGroup {
    return this.formBuilder.group({
      oeName: [
        this.organization && this.organization.oeName ? this.organization.oeName : '',
        [Validators.required]
      ],
      addressLine1: [this.address.addressLine1 ? this.address.addressLine1 : '', [Validators.required]],
      addressLine2: [this.address.addressLine2 ? this.address.addressLine2 : ''],
      city: [this.address.city ? this.address.city : '', [Validators.required]],
      state: [
        this.address.state ? this.address.state.replace(/\s/g, '') : '-',
        [Validators.required, Validators.pattern('[A-Za-z]{2}')]
      ],
      zip: [this.address.zip ? this.address.zip : '', [Validators.required, Validators.minLength(5)]],
      country: [
        this.countryPayload,
        [Validators.required, Validators.pattern('[A-Za-z]{2}')]
      ],
    });
  }

  success() {
    this.updated.emit();
  }

  initializeStates(): Array<any> {
    return [
      {
        options: [
          { name: '', value: '-', selected: this.setStateOrProvince('-') },
          { name: 'AK', value: 'AK', selected: this.setStateOrProvince('AK') },
          { name: 'AL', value: 'AL', selected: this.setStateOrProvince('AL') },
          { name: 'AR', value: 'AR', selected: this.setStateOrProvince('AR') },
          { name: 'AZ', value: 'AZ', selected: this.setStateOrProvince('AZ') },
          { name: 'CA', value: 'CA', selected: this.setStateOrProvince('CA') },
          { name: 'CO', value: 'CO', selected: this.setStateOrProvince('CO') },
          { name: 'CT', value: 'CT', selected: this.setStateOrProvince('CT') },
          { name: 'DC', value: 'DC', selected: this.setStateOrProvince('DC') },
          { name: 'DE', value: 'DE', selected: this.setStateOrProvince('DE') },
          { name: 'FL', value: 'FL', selected: this.setStateOrProvince('FL') },
          { name: 'GA', value: 'GA', selected: this.setStateOrProvince('GA') },
          { name: 'HI', value: 'HI', selected: this.setStateOrProvince('HI') },
          { name: 'IA', value: 'IA', selected: this.setStateOrProvince('IA') },
          { name: 'ID', value: 'ID', selected: this.setStateOrProvince('ID') },
          { name: 'IL', value: 'IL', selected: this.setStateOrProvince('IL') },
          { name: 'IN', value: 'IN', selected: this.setStateOrProvince('IN') },
          { name: 'KS', value: 'KS', selected: this.setStateOrProvince('KS') },
          { name: 'KY', value: 'KY', selected: this.setStateOrProvince('KY') },
          { name: 'LA', value: 'LA', selected: this.setStateOrProvince('LA') },
          { name: 'MA', value: 'MA', selected: this.setStateOrProvince('MA') },
          { name: 'MD', value: 'MD', selected: this.setStateOrProvince('MD') },
          { name: 'ME', value: 'ME', selected: this.setStateOrProvince('ME') },
          { name: 'MI', value: 'MI', selected: this.setStateOrProvince('MI') },
          { name: 'MN', value: 'MN', selected: this.setStateOrProvince('MN') },
          { name: 'MO', value: 'MO', selected: this.setStateOrProvince('MO') },
          { name: 'MS', value: 'MS', selected: this.setStateOrProvince('MS') },
          { name: 'MT', value: 'MT', selected: this.setStateOrProvince('MT') },
          { name: 'NC', value: 'NC', selected: this.setStateOrProvince('NC') },
          { name: 'ND', value: 'ND', selected: this.setStateOrProvince('ND') },
          { name: 'NE', value: 'NE', selected: this.setStateOrProvince('NE') },
          { name: 'NH', value: 'NH', selected: this.setStateOrProvince('NH') },
          { name: 'NJ', value: 'NJ', selected: this.setStateOrProvince('NJ') },
          { name: 'NM', value: 'NM', selected: this.setStateOrProvince('NM') },
          { name: 'NV', value: 'NV', selected: this.setStateOrProvince('NV') },
          { name: 'NY', value: 'NY', selected: this.setStateOrProvince('NY') },
          { name: 'OH', value: 'OH', selected: this.setStateOrProvince('OH') },
          { name: 'OK', value: 'OK', selected: this.setStateOrProvince('OK') },
          { name: 'OR', value: 'OR', selected: this.setStateOrProvince('OR') },
          { name: 'PA', value: 'PA', selected: this.setStateOrProvince('PA') },
          { name: 'RI', value: 'RI', selected: this.setStateOrProvince('RI') },
          { name: 'SC', value: 'SC', selected: this.setStateOrProvince('SC') },
          { name: 'SD', value: 'SD', selected: this.setStateOrProvince('SD') },
          { name: 'TN', value: 'TN', selected: this.setStateOrProvince('TN') },
          { name: 'TX', value: 'TX', selected: this.setStateOrProvince('TX') },
          { name: 'UT', value: 'UT', selected: this.setStateOrProvince('UT') },
          { name: 'VA', value: 'VA', selected: this.setStateOrProvince('VA') },
          { name: 'VT', value: 'VT', selected: this.setStateOrProvince('VT') },
          { name: 'WA', value: 'WA', selected: this.setStateOrProvince('WA') },
          { name: 'WI', value: 'WI', selected: this.setStateOrProvince('WI') },
          { name: 'WV', value: 'WV', selected: this.setStateOrProvince('WV') },
          { name: 'WY', value: 'WY', selected: this.setStateOrProvince('WY') }
        ]
      }
    ];
  }

  initializeProvinces(): Array<any> {
    return [
      {
        options: [
          { name: '', value: '-', selected: this.setStateOrProvince('-') },
          { name: 'AB', value: 'AB', selected: this.setStateOrProvince('AB') },
          { name: 'BC', value: 'BC', selected: this.setStateOrProvince('BC') },
          { name: 'MB', value: 'MB', selected: this.setStateOrProvince('MB') },
          { name: 'NB', value: 'NB', selected: this.setStateOrProvince('NB') },
          { name: 'NL', value: 'NL', selected: this.setStateOrProvince('NL') },
          { name: 'NS', value: 'NS', selected: this.setStateOrProvince('NS') },
          { name: 'NT', value: 'NT', selected: this.setStateOrProvince('NT') },
          { name: 'NU', value: 'NU', selected: this.setStateOrProvince('NU') },
          { name: 'ON', value: 'ON', selected: this.setStateOrProvince('ON') },
          { name: 'PE', value: 'PE', selected: this.setStateOrProvince('PE') },
          { name: 'QC', value: 'QC', selected: this.setStateOrProvince('QC') },
          { name: 'SK', value: 'SK', selected: this.setStateOrProvince('SK') },
          { name: 'YT', value: 'YT', selected: this.setStateOrProvince('YT') }
        ]
      }
    ];
  }

  getTranslations(){
    this.translocoService.selectTranslateObject('profileManagement.addressChangeForm').pipe(takeUntil(this.destroyed$)).subscribe(translate => {
      //we have to create a new array/object whenever user switches language and country dropdown shows translated name
      this.states[0]['options'][0].name = translate.textSelect;
      this.provinces[0]['options'][0].name = translate.textSelect;
    });
  }
}
